import { getSupabaseClient } from '@/api/supabaseClient';
import { clearAppIconBadge } from '@/lib/appIconBadge';
import { getNotificationRoute } from '@/lib/notificationRouting';
import { toast } from 'sonner';

let listenersAttached = false;
let initializationPromise = null;
let initializedUserId = null;
let currentContext = {
  navigate: null,
  user: null,
  member: null,
};
const PUSH_CHANNEL_ID = 'alerts_v2';
const PUSH_DEVICE_TOKEN_STORAGE_KEY = 'mbv_push_device_token';

function getCapacitorPlugin(name) {
  if (typeof window === 'undefined') return null;
  return window.Capacitor?.Plugins?.[name] || window.Capacitor?.[name] || null;
}

function isNativePlatform() {
  if (typeof window === 'undefined') return false;
  if (typeof window.Capacitor?.isNativePlatform === 'function') {
    return window.Capacitor.isNativePlatform();
  }
  return Boolean(window.Capacitor?.Plugins?.PushNotifications);
}

async function getDeviceContext() {
  const Device = getCapacitorPlugin('Device');
  if (!Device?.getInfo) {
    return {
      platform: 'unknown',
      appVersion: null,
      isVirtual: false,
    };
  }

  const info = await Device.getInfo().catch(() => ({}));

  return {
    platform: String(info.platform || 'unknown'),
    appVersion: String(info.appVersion || info.osVersion || ''),
    isVirtual: Boolean(info.isVirtual),
  };
}

async function persistPushToken(tokenValue) {
  if (!tokenValue) return;

  localStorage.setItem(PUSH_DEVICE_TOKEN_STORAGE_KEY, tokenValue);

  const supabase = getSupabaseClient();
  const { platform, appVersion } = await getDeviceContext();
  const memberEmail = currentContext.member?.email || currentContext.user?.email || null;

  const { error } = await supabase.rpc('register_push_device', {
    p_app_version: appVersion || null,
    p_device_id: null,
    p_device_token: tokenValue,
    p_member_email: memberEmail,
    p_member_id: currentContext.member?.id || null,
    p_platform: platform.toLowerCase(),
  });

  if (error) {
    throw new Error(`Falha ao salvar token push: ${error.message}`);
  }

  console.info('[push] token registrado no Supabase', {
    platform: platform.toLowerCase(),
    memberId: currentContext.member?.id || null,
    userId: currentContext.user?.id || null,
  });
}

function navigateFromPush(notification) {
  const navigate = currentContext.navigate;
  if (typeof navigate !== 'function') return;

  const payload = notification?.data || {};
  const route = getNotificationRoute({
    route: payload.route,
    type: payload.type,
    reference_id: payload.reference_id || payload.referenceId,
  });

  if (route) navigate(route);
}

async function attachPushListeners() {
  if (listenersAttached || !isNativePlatform()) return;

  const PushNotifications = getCapacitorPlugin('PushNotifications');
  if (!PushNotifications?.addListener) return;

  listenersAttached = true;

  try {
    await PushNotifications.addListener('registration', async (token) => {
      try {
        console.info('[push] token recebido do dispositivo');
        await persistPushToken(String(token?.value || ''));
      } catch (error) {
        console.error(error);
        toast.error('Falha ao registrar push no Supabase');
      }
    });

    await PushNotifications.addListener('registrationError', (error) => {
      console.error('Erro ao registrar push notification:', error);
      toast.error('Erro de registro push no dispositivo');
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      const title = notification?.title || 'Nova notificacao';
      const body = notification?.body || '';
      toast.info(body ? `${title}: ${body}` : title);
      clearAppIconBadge().catch((error) => {
        console.warn('[badge] falha ao limpar badge apos push', error);
      });
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
      clearAppIconBadge().catch((error) => {
        console.warn('[badge] falha ao limpar badge apos abrir push', error);
      });
      navigateFromPush(event?.notification);
    });
  } catch (error) {
    listenersAttached = false;
    throw new Error(`Falha ao conectar listeners de push: ${error?.message || error}`);
  }
}

export async function initializeNativePushNotifications({ user, member, navigate }) {
  currentContext = { user, member, navigate };

  if (!user?.id || !isNativePlatform()) {
    console.info('[push] inicializacao ignorada (sem usuario ou fora de plataforma nativa)');
    return { enabled: false, reason: 'not-native' };
  }

  const PushNotifications = getCapacitorPlugin('PushNotifications');
  if (!PushNotifications?.register) return { enabled: false, reason: 'plugin-missing' };

  if (initializedUserId === user.id) {
    return { enabled: true, reason: 'already-initialized' };
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    const deviceContext = await getDeviceContext();
    if (deviceContext.platform.toLowerCase() === 'android' && deviceContext.isVirtual) {
      console.info('[push] registro ignorado no emulador Android');
      return { enabled: false, reason: 'android-emulator' };
    }

    if (typeof PushNotifications.createChannel === 'function') {
      try {
        await PushNotifications.createChannel({
          id: PUSH_CHANNEL_ID,
          name: 'Notificacoes',
          description: 'Notificacoes gerais do app MBV',
          importance: 5,
          visibility: 1,
          sound: 'default',
        });
      } catch (_error) {
        // Some platforms do not expose channels.
      }
    }

    await attachPushListeners();

    let currentPermissions = { receive: 'prompt' };
    if (typeof PushNotifications.checkPermissions === 'function') {
      try {
        currentPermissions = await PushNotifications.checkPermissions();
      } catch (error) {
        console.warn('[push] falha ao verificar permissao de notificacao', error);
      }
    }

    let receivePermission = currentPermissions?.receive || 'prompt';
    if (receivePermission === 'prompt' && typeof PushNotifications.requestPermissions === 'function') {
      try {
        const requestedPermissions = await PushNotifications.requestPermissions();
        receivePermission = requestedPermissions?.receive || receivePermission;
      } catch (error) {
        throw new Error(`Falha ao solicitar permissao de notificacao: ${error?.message || error}`);
      }
    }

    if (receivePermission !== 'granted') {
      console.warn('[push] permissao de notificacao negada');
      return { enabled: false, reason: 'permission-denied' };
    }

    console.info('[push] registrando dispositivo para push');

    try {
      await PushNotifications.register();
      initializedUserId = user.id;
      return { enabled: true };
    } catch (error) {
      throw new Error(`Falha ao registrar dispositivo para push: ${error?.message || error}`);
    }
  })();

  try {
    return await initializationPromise;
  } finally {
    initializationPromise = null;
  }
}
